<template>
  <div v-if="isActive" class="bloc-info">
    <div class="bloc-info-inner">

      <div class="bloc-accent-line"></div>

      <div class="bloc-content">
        <div v-if="iconSrc" class="bloc-icon-wrap">
          <img :src="iconSrc" alt="Icône info" class="bloc-icon" />
        </div>

        <div class="bloc-text">
          <span class="bloc-eyebrow">{{ t('info_eyebrow') }}</span>
          <p class="bloc-message" v-html="translatedMessage"></p>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useLocale } from '@/i18n'
import { translateOne } from '@/utils/translateBatch'

const props = defineProps({
  isActive: Boolean,
  message: String,
  iconeSelectionnee: String,
})

const { t, locale } = useLocale()

const iconSrc = computed(() =>
  props.iconeSelectionnee ? `/img/${props.iconeSelectionnee}.png` : null
)

const translatedMessage = ref(props.message)

async function translateMessage() {
  if (locale.value === 'fr' || !props.message) {
    translatedMessage.value = props.message
    return
  }
  const cacheKey = `bs_tr_info_${locale.value}`
  let cached = {}
  try { cached = JSON.parse(localStorage.getItem(cacheKey) || '{}') } catch {}
  if (cached.source === props.message) {
    translatedMessage.value = cached.translated
    return
  }
  const translated = await translateOne(props.message, 'fr', locale.value)
  translatedMessage.value = translated || props.message
  try {
    localStorage.setItem(cacheKey, JSON.stringify({ source: props.message, translated: translatedMessage.value }))
  } catch {}
}

watch([locale, () => props.message], translateMessage, { immediate: true })
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;1,400&family=Inter:wght@300;400;500&display=swap');

.bloc-info {
  width: 100%;
  box-sizing: border-box;
  position: relative;
  z-index: 700000;
  animation: blocReveal 4.5s linear 4.5s both;
}

@keyframes blocReveal {
  0%   { opacity: 0; transform: translateY(-88vh); }
  7%   { opacity: 1; transform: translateY(-88vh); }
  74%  { opacity: 1; transform: translateY(-88vh); animation-timing-function: cubic-bezier(0.22, 1, 0.36, 1); }
  100% { opacity: 1; transform: translateY(0); }
}

.bloc-info-inner {
  width: 100%;
  margin: 0;
  background: linear-gradient(135deg, #1a1912 0%, #14130e 100%);
  border-top: 1px solid rgba(201, 168, 76, 0.35);
  overflow: hidden;
  position: relative;
}


.bloc-accent-line {
  height: 3px;
  background: linear-gradient(90deg, transparent, #c9a84c 30%, #c9a84c 70%, transparent);
}

.bloc-content {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1.5rem 2rem;
  max-width: 960px;
  margin: 0 auto;
}

.bloc-icon-wrap {
  flex-shrink: 0;
  width: 72px;
  height: 72px;
  border-radius: 10px;
  border: 1px solid rgba(201, 168, 76, 0.25);
  background: rgba(201, 168, 76, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  box-sizing: border-box;
}

.bloc-icon {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.bloc-text {
  flex: 1;
  min-width: 0;
}

.bloc-eyebrow {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: #c9a84c;
  margin-bottom: 0.4rem;
}

.bloc-message {
  margin: 0;
  font-family: 'Playfair Display', serif;
  font-size: 1.1rem;
  font-weight: 400;
  color: #ede8df;
  line-height: 1.65;
}

.bloc-message :deep(span) {
  color: #c9a84c;
}

@media (max-width: 600px) {
  .bloc-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    padding: 1.25rem 1.25rem;
  }

  .bloc-icon-wrap { width: 56px; height: 56px; }

  .bloc-message { font-size: 0.95rem; }
}
</style>
